import {Link} from 'react-router';
import moment from 'moment';
import React, {useMemo} from 'react';

import AssignmentIcon from './AssignmentIcon';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import AssignmentStatusBadge from './AssignmentStatusBadge';
import {getSubmissionStatus} from 'client/AssignmentPracticeView/assignmentPracticeView.utils';
import {Text} from '@albert-io/atomic';
import {pluralize} from 'lib/stringUtils';
import type {TextColor} from '@albert-io/atomic/atoms/Text/Text.react';

interface Props {
  assignment: AssignmentModelV3;
}

export default function Assignment({assignment}: Props) {
  const dueDate = useMemo(() => moment(assignment.getDueDateFromSettings()), [assignment]);
  const isPastDue = dueDate.isBefore(moment());

  const dueCopyColor = useMemo<TextColor>(() => {
    const diffMinutes = dueDate.diff(moment(), 'minutes');

    if (diffMinutes < 0) {
      return 'negative';
    } else if (diffMinutes >= 0 && diffMinutes <= 59) {
      return 'warning';
    } else {
      return 'primary';
    }
  }, [dueDate]);

  const dueCopyText = useMemo(() => {
    const now = moment();

    const diffMinutes = Math.abs(dueDate.diff(now, 'minutes'));
    const diffHours = Math.abs(dueDate.diff(now, 'hours'));
    const diffDays = Math.abs(dueDate.diff(now, 'days'));
    const diffWeeks = Math.abs(dueDate.diff(now, 'weeks'));
    const diffMonths = Math.abs(dueDate.diff(now, 'months'));

    if (isPastDue) {
      if (diffMinutes <= 59) {
        return `Due ${diffMinutes} ${pluralize('minute', diffMinutes)} ago`;
      } else if (diffHours < 24) {
        return `Due ${diffHours} ${pluralize('hour', diffHours)} ago`;
      } else if (diffHours < 48) {
        return 'Due yesterday';
      } else if (diffDays < 7) {
        return `Due ${diffDays} ${pluralize('day', diffDays)} ago`;
      } else if (diffMonths < 1) {
        return `Due ${diffWeeks} ${pluralize('week', diffWeeks)} ago`;
      } else {
        return `Due ${diffMonths} ${pluralize('month', diffMonths)} ago`;
      }
    } else {
      if (diffMinutes < 60) {
        return `Due in ${diffMinutes} ${pluralize('minute', diffMinutes)}`;
      } else if (diffHours < 4) {
        return `Due in ${diffHours} ${pluralize('hour', diffHours)}`;
      } else if (dueDate.isBefore(moment().endOf('day'))) {
        return 'Due today';
      } else if (dueDate.isBefore(moment().add(1, 'day').endOf('day'))) {
        return 'Due tomorrow';
      } else if (dueDate.isBefore(moment().endOf('week'))) {
        return `Due ${dueDate.format('dddd')}`;
      } else if (
        dueDate.isAfter(moment().endOf('week')) &&
        dueDate.isBefore(moment().add(1, 'week').endOf('week'))
      ) {
        return 'Due next week';
      }
      return `Due ${dueDate.format('MMMM Do')}`;
    }
  }, [dueDate, isPastDue]);

  return (
    <Link
      className='flex items-center gap-x2 p-x0-half no-underline'
      to={`/assignment/${assignment.getId()}`}
    >
      <AssignmentIcon backgroundColorClass='background-brand-subtle-hover' iconColor='brand' />
      <div className='flex flex-1 flex-col'>
        <Text>{assignment.getName()}</Text>
        <Text color={dueCopyColor}>{dueCopyText}</Text>
      </div>
      <AssignmentStatusBadge isPastDue={isPastDue} status={getSubmissionStatus({assignment})} />
    </Link>
  );
}
